'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { addEditActivity, addActivityNote, addViewActivity, getActivities } from 'src/lib/utils'
import { ActivityResponse, APIResponse } from 'types/types'

import { useRequestCallback } from '@fsg/next-auth'

import { UseFormReturn } from '@app/components/AppForm'
import { ACTIVITY_ENTITIES, ACTIVITY_TYPES, ENDPOINTS } from '@app/constants'
import { formatCamelCaseLabel } from '@app/utils'
import { useQueries } from './useQueries'


export function useActivityTracking<T = any>(appForm: UseFormReturn<T>, entity: ACTIVITY_ENTITIES, id: string) {
  const router = useRouter();
  const { data: sessionData } = useSession();
  const { request } = useRequestCallback();
  const [newActivity, setNewActivity] = useState(0);
  const [loading, setLoading] = useState(true);
  const isHandlingViewActivity = useRef(false);
  const isInitialized = useRef(false)
  const user = useMemo(() => sessionData?.user, [sessionData]);
  const { form } = appForm;
  const [activities, setActivities] = useState<ActivityResponse[]>([]);

  const logEditActivity = async () => {
    const { isDirty, dirtyFields } = form.formState;
    const editedFields = Object.keys(dirtyFields).filter(
      (field) => field !== 'id' && field !== 'createdAt' && field !== 'updatedAt'
    );
    const parsedFields = editedFields.map((field) => ` ${formatCamelCaseLabel(field)}`);

    if (!isDirty || parsedFields.length === 0) return;

    const activity = await addEditActivity(request, entity, id);
    const content = `Fields Edited:${parsedFields.toString()}`;
    await addActivityNote(request, activity.id, content);
    setNewActivity((prev) => prev + 1);

  };

  const handleViewActivity = useCallback(async (activities: ActivityResponse[]) => {
    if (isHandlingViewActivity.current) return;
    isHandlingViewActivity.current = true;
    function isToday(input: Date | string): boolean {
      const now = new Date();
      const date = typeof input === 'string' ? new Date(input) : input;
      return (
        date.getUTCDate() === now.getUTCDate() &&
        date.getUTCMonth() === now.getUTCMonth() &&
        date.getUTCFullYear() === now.getUTCFullYear()
      );
    }
    try {
      if (id !== undefined) {
        const sortedActivities = activities.sort((a: any, b: any) => {
          return new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime();
        });
        const lastActivity = sortedActivities.find(
          (activity: ActivityResponse) =>
            (activity.id === user?.id || activity.user?.email === user?.email) &&
            activity.activityType === ACTIVITY_TYPES.VIEW_ONLY
        );
        if (lastActivity && isToday(lastActivity.recordedAt)) {
          const content = 'Recurring view';
          await addActivityNote(request, lastActivity.id, content);
        } else {
          const activity = await addViewActivity(request, entity, id);
          const content = 'First view';
          await addActivityNote(request, activity.id, content);

        }
      }
    } finally {
      setNewActivity((prev) => prev + 1);
      isHandlingViewActivity.current = false;
    }
  }, [entity, id, user?.email, user?.id]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await request({
        path: `${ENDPOINTS.ACTIVITIES(entity, id)}`,
      }) as APIResponse<ActivityResponse>;
      setActivities(data.data);
      return data.data
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }

  async function initializeActivityTracking() {
    const initActivities = await fetchActivities()
    await handleViewActivity(initActivities)
    isInitialized.current = true
  }

  useEffect(() => {
    if (!isInitialized.current) {
      initializeActivityTracking();
    }
  }, [])

  //refresh activities store when new activity is detected
  useEffect(() => {
    if (newActivity > 0) {
      fetchActivities();
    }
  }, [newActivity]);

  return { logEditActivity, activities, loading, setActivities };
}

